import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Field, formValueSelector } from 'redux-form'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import pDebounce from 'p-debounce'

import { getMomentTimeZone } from '../../../../service/locale/timezone'
import { Card } from '../../../../component-library/components/card/card.component'
import { StyledHeading } from '../order-form.component.styles'
import { ConnectedPickupTimeWithTranslation } from './pickup-time/pickup-time.component'
import { DropoffTimeWithTranslation } from './dropoff-time/dropoff-time.component'
import { requiredField } from '../../../../service/validation'
import { apiService } from '../../../api/api.service'
import { orderTimeService } from '../../../order/order-time.service'
import { deliveryDropoffTime } from '../../../duck/actions'

const currentTimeZone = getMomentTimeZone()
const rerenderInterval = 30000
const getCurrentISOTime = () => moment.tz(currentTimeZone).format()

const ScheduleForm = ({
  pickupTime,
  shipper,
  recipient,
  setDeliveryDropoffTime,
  dataTestid,
}) => {
  const debouncedGetPricingOptions = useMemo(
    () => pDebounce(apiService.getPricingOptions, 300),
    []
  )
  const [currentISOTime, setCurrentISOTime] = useState(getCurrentISOTime())
  const [t] = useTranslation()
  const pickupTimeValidator = useMemo(
    () => requiredField(t('error.missing.pickupTime')),
    [t]
  )
  const dropoffTimeValidator = useMemo(
    () => requiredField(t('error.missing.dropoffTime')),
    [t]
  )

  const initialValues = {
    shipperRegionSupported: null,
    shipperRegion: null,
    recipientRegionSupported: null,
    recipientRegion: null,
    pickupTimeValid: null,
    dropoffTime: null,
  }

  const [instant, setInstant] = useState({ ...initialValues })
  const [speed, setSpeed] = useState({ ...initialValues })
  const [sameDayDay, setSameDayDay] = useState({ ...initialValues })
  const [sameDayNight, setSameDayNight] = useState({ ...initialValues })

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentISOTime(getCurrentISOTime())
    }, rerenderInterval)
    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    const fetchPricingOptions = async () => {
      const validShipperAndRecipient = () => {
        if (!shipper || !recipient || !pickupTime) {
          return false
        } else if (
          !shipper.latitude ||
          !shipper.longitude ||
          !recipient.latitude ||
          !recipient.longitude
        ) {
          return false
        }
        return !(
          isNaN(shipper.latitude) ||
          isNaN(shipper.longitude) ||
          isNaN(recipient.longitude) ||
          isNaN(recipient.latitude)
        )
      }

      if (validShipperAndRecipient()) {
        try {
          const {
            request_at: convertNowToPickupTime,
          } = orderTimeService.getUpdatedPickupDropoffTime({
            requestAt: pickupTime,
          })
          const {
            latitude: shipperLatitude,
            longitude: shipperLongitude,
          } = shipper
          const {
            latitude: recipientLatitude,
            longitude: recipientLongitude,
          } = recipient
          const order = {
            shipper: { latitude: shipperLatitude, longitude: shipperLongitude },
            recipient: {
              latitude: recipientLatitude,
              longitude: recipientLongitude,
            },
            pickup_time: convertNowToPickupTime,
          }

          const result = await debouncedGetPricingOptions(order)
          const [
            instantQuotation,
            speedQuotation,
            sameDayDayQuotation,
            sameDayNightQuotation,
          ] = result.delivery_quotation_types

          setDeliveryDropoffTime({
            instant: instantQuotation.dropoff_time,
            speed: speedQuotation.dropoff_time,
            same_day_day: sameDayDayQuotation.dropoff_time,
            same_day_night: sameDayNightQuotation.dropoff_time,
          })

          setInstant({
            shipperRegionSupported: instantQuotation.shipper.service_available,
            shipperRegion: instantQuotation.shipper.zone_name,
            recipientRegionSupported:
              instantQuotation.recipient.service_available,
            recipientRegion: instantQuotation.recipient.zone_name,
            pickupTimeValid: instantQuotation.pickup_time_valid,
            dropoffTime: instantQuotation.dropoff_time,
          })

          setSpeed({
            shipperRegionSupported: speedQuotation.shipper.service_available,
            shipperRegion: speedQuotation.shipper.zone_name,
            recipientRegionSupported:
              speedQuotation.recipient.service_available,
            recipientRegion: speedQuotation.recipient.zone_name,
            pickupTimeValid: speedQuotation.pickup_time_valid,
            dropoffTime: speedQuotation.dropoff_time,
          })

          setSameDayDay({
            shipperRegionSupported:
              sameDayDayQuotation.shipper.service_available,
            shipperRegion: sameDayDayQuotation.shipper.zone_name,
            recipientRegionSupported:
              sameDayDayQuotation.recipient.service_available,
            recipientRegion: sameDayDayQuotation.recipient.zone_name,
            pickupTimeValid: sameDayDayQuotation.pickup_time_valid,
            dropoffTime: sameDayDayQuotation.dropoff_time,
          })

          setSameDayNight({
            shipperRegionSupported:
              sameDayNightQuotation.shipper.service_available,
            shipperRegion: sameDayNightQuotation.shipper.zone_name,
            recipientRegionSupported:
              sameDayNightQuotation.recipient.service_available,
            recipientRegion: sameDayNightQuotation.recipient.zone_name,
            pickupTimeValid: sameDayNightQuotation.pickup_time_valid,
            dropoffTime: sameDayNightQuotation.dropoff_time,
          })
        } catch (error) {
          console.log(error)
        }
      } else {
        setInstant({ ...initialValues })
        setSpeed({ ...initialValues })
        setSameDayDay({ ...initialValues })
        setSameDayNight({ ...initialValues })
      }
    }

    fetchPricingOptions()
  }, [recipient, shipper, currentISOTime, pickupTime])

  return (
    <Card data-testid={dataTestid}>
      <StyledHeading>
        {t('orders.new.basicInfo.subtitle.scheduleTime')}
      </StyledHeading>
      <Field
        name="pickup_time"
        component={ConnectedPickupTimeWithTranslation}
        validate={pickupTimeValidator}
        currentISOTime={currentISOTime}
      />

      <Field
        name="delivery_quotation_type"
        component={DropoffTimeWithTranslation}
        validate={dropoffTimeValidator}
        instant={instant}
        speed={speed}
        sameDayDay={sameDayDay}
        sameDayNight={sameDayNight}
      />
    </Card>
  )
}

ScheduleForm.propTypes = {
  pickupTime: PropTypes.string,
  shipper: PropTypes.shape({
    latitude: PropTypes.string,
    longitude: PropTypes.string,
  }),
  recipient: PropTypes.shape({
    latitude: PropTypes.string,
    longitude: PropTypes.string,
  }),
  setDeliveryDropoffTime: PropTypes.func,
  dataTestid: PropTypes.string,
}

const selector = formValueSelector('delivery-order')

const mapStateToProps = state => ({
  pickupTime: selector(state, 'pickup_time'),
  shipper: selector(state, 'shipper'),
  recipient: selector(state, 'recipient'),
})

const mapDispatchToProps = dispatch => ({
  setDeliveryDropoffTime: ({ speed, instant, same_day_day, same_day_night }) =>
    dispatch(
      deliveryDropoffTime({ speed, instant, same_day_day, same_day_night })
    ),
})

const ConnectedScheduleForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(ScheduleForm)

export { ConnectedScheduleForm }
